// warehouse.md's № 244 act-requisites shape and validation — ported from the
// wireframe's `otk/wireframes/src/store/warehouse.ts` (`ActRequisitesInput`/
// `actRequisitesFailure`), byte-for-byte on the validation rules themselves.
// This project has no `destroyBlanksByAct` backend endpoint yet (warehouse.md
// §1b: "no wire format" is a deliberate, documented gap — open question 104),
// so this validation is what actually gates the dialog's finalizer client-side
// today, same role the wireframe's store gave it ("the dialog's own
// validation... is the interface, not the gate" — here there is no further
// gate behind it yet, which is exactly the gap).

export interface CommissionMember {
  role: string
  name: string
}

export interface ActRequisitesInput {
  actDate: string
  dateFrom: string
  dateTo: string
  location: string
  chair: CommissionMember
  members: CommissionMember[]
  destructionPlace: string
  destructionTime: string
  destructionMethod: string
  /** Орган, який видав бланки, — отримувач копії акта (п. 12, абз. другий). */
  territorialBody: string
}

export function emptyMember(): CommissionMember {
  return { role: "", name: "" }
}

export function emptyActRequisites(): ActRequisitesInput {
  return {
    actDate: "",
    dateFrom: "",
    dateTo: "",
    location: "",
    chair: emptyMember(),
    members: [emptyMember(), emptyMember()],
    destructionPlace: "",
    destructionTime: "",
    destructionMethod: "",
    territorialBody: "",
  }
}

const filledMember = (m?: CommissionMember) =>
  Boolean(m && m.role.trim() && m.name.trim())
const filled = (v?: string) => Boolean(v && v.trim())

/**
 * Справжня дата григоріанського календаря в точному форматі `yyyy-MM-dd` —
 * не лише вигляд: `2026-02-30` має правильну форму, але такого дня не існує.
 * Свідомо без `Date.parse`/`new Date()`, які мовчки нормалізують переповнення.
 */
export function isCalendarDate(v?: string): boolean {
  if (!v) return false
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
  if (!m) return false
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  if (month < 1 || month > 12 || day < 1) return false
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
  const daysInMonth = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  return day <= daysInMonth[month - 1]
}

/** Час доби в суворому 24-годинному `HH:mm`: `00:00`–`23:59`. */
export const isClockTime = (v?: string) =>
  Boolean(v && /^([01]\d|2[0-3]):[0-5]\d$/.test(v))

export type ActRequisitesFailure = "INCOMPLETE_ACT"

/**
 * Повнота реквізитів форми № 244 — одне правило для обох режимів діалогу.
 * Перевіряє повноту й формальну дійсність (дати — справжні календарні дні,
 * час — `HH:mm`), а не правдоподібність.
 */
export function actRequisitesFailure(
  act: ActRequisitesInput
): ActRequisitesFailure | null {
  if (!isCalendarDate(act.actDate)) return "INCOMPLETE_ACT"
  if (!isCalendarDate(act.dateFrom) || !isCalendarDate(act.dateTo)) {
    return "INCOMPLETE_ACT"
  }
  if (act.dateFrom > act.dateTo) return "INCOMPLETE_ACT"
  if (!filled(act.location)) return "INCOMPLETE_ACT"
  if (!filledMember(act.chair)) return "INCOMPLETE_ACT"
  if (act.members.filter(filledMember).length < 2) return "INCOMPLETE_ACT"
  if (
    !filled(act.destructionPlace) ||
    !isClockTime(act.destructionTime) ||
    !filled(act.destructionMethod)
  ) {
    return "INCOMPLETE_ACT"
  }
  return null
}

/**
 * The unmet-requisite list for the dialog's own "залишилось заповнити" line
 * — ported verbatim from the wireframe's `actRequisitesMissing`. Does not
 * include the reason/attestation/mark-journal/copy-acknowledgement items —
 * those are dialog-local state, added by the caller alongside this list
 * (same composition as `DestructionActDialog.tsx`'s own `missing` array).
 */
export function actRequisitesMissing(act: ActRequisitesInput): string[] {
  const missing: string[] = []
  if (!act.actDate) missing.push("дата складання")
  if (!act.dateFrom || !act.dateTo) missing.push("період накопичення")
  if (act.dateFrom && act.dateTo && act.dateFrom > act.dateTo) {
    missing.push("період накопичення (початок пізніше за кінець)")
  }
  if (!filled(act.location)) missing.push("місце складання")
  if (!filledMember(act.chair)) missing.push("голова комісії (посада і ПІБ)")
  if (act.members.filter(filledMember).length < 2) {
    missing.push("двоє членів комісії (посада і ПІБ кожного)")
  }
  if (!filled(act.destructionPlace)) missing.push("місце знищення")
  if (!filled(act.destructionTime)) missing.push("час знищення")
  if (!filled(act.destructionMethod)) missing.push("спосіб знищення")
  return missing
}
